import { memo, useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import styled, { css } from 'styled-components'
import { Box } from '@machobear/design-system'
import { animated } from '@react-spring/web'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import noop from 'lodash/noop'

import structure from '@/structure'
import { breakpoints, colors, fontSizes } from '@/styles'
import { isEmptyOrNull } from '@/utils'
import useMediaQuery from '@/hooks/use-media-query'
import EmptyState from '@/features/Listings/empty-state'
import useListingsFavourites from '@/features/Listings/hooks/use-listings-favourites'
import { SearchResultsContent } from '@/features/Listings/seo'
import { CityBreadcrumbs } from '@/components/breadcrumbs'
import Pagination from '@/components/pagination'
import SearchTitle from '@/components/search-title'
import { ParagraphRegular } from '@/components/text'

import { useMapList } from '../maplist-context'
import { MessageModalProvider } from './context'
import FeaturedListings from './featured-listings'
import RegularListings from './regular-listings'

const ListWrapper = styled(animated.div)`
  width: 100%;
  overflow-x: hidden;
  display: grid;
  align-content: start;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    position: block;
    height: ${props =>
      props.hasActiveFilters
        ? `calc(100vh - ${structure.map.headerMobile}px - ${structure.map.activeFilters}px)`
        : `calc(100vh)`};
    top: ${props =>
      props.$hasActiveFilters
        ? `${structure.map.headerMobile + structure.map.activeFilters}`
        : `${structure.map.headerMobile}`}px;

    ${props =>
      props.hasActiveFilters &&
      `
        height: calc(100vh - ${structure.map.headerMobile}px - ${structure.map.activeFilters}px + 80px);
    `}

    left: 0;
    right: 0;
    z-index: 10000;
    background: transparent;
  }
`

const SearchTitleWrapper = styled.div`
  padding: 24px 20px 0;
`

const Content = styled.div`
  padding: 24px 20px 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: ${`calc(${structure.map.listWidthTriple}px - 15px)`};

  @media (max-width: ${breakpoints.mapLarge}) {
    width: ${`calc(${structure.map.listWidthDouble}px - 15px)`};
  }

  @media (max-width: ${breakpoints.map}) {
    width: ${`calc(${structure.map.listWidthSingle}px - 15px)`};
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
  }
`

const NearbyListingsText = styled(ParagraphRegular)`
  font-weight: 500;
`

const PaginationWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;

  @media (max-width: ${breakpoints.map}) {
    max-width: 325px;
    margin: 0 auto;
  }
`

const PaginationSummary = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0 50px;
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  font-weight: 600;
  letter-spacing: -0.2px;
  line-height: 22px;
`

const NoResultsWrapper = styled.div`
  padding: 0 20px;
  width: ${structure.map.listWidthTriple}px;
  @media (max-width: ${breakpoints.mapLarge}) {
    width: ${structure.map.listWidthDouble}px;
  }
  @media (max-width: ${breakpoints.map}) {
    width: ${structure.map.listWidthSingle}px;
  }
  @media (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100vw;
  }
`

const pageButtonsStyle = css`
  @media (max-width: ${breakpoints.map}) {
    margin: 0 2px;
  }
`

const List = memo(
  ({
    onlyVerifiedListings,
    setHighlightedBuilding = noop,
    preload = noop,
    markersLoaded,
    setVisitedBuilding,
    customTitle,
    customBreadcrumbs,
    hideFeaturedListings,
    hideSeoContent,
    hideEmptyState,
    metadata,
    buildings,
    loadingBuildings,
    featured,
    data,
    neighborhood,
    city,
    cityLoading,
    citySlug,
    filterSlug,
    filter,
    isFilterSet
  }) => {
    const { t } = useTranslation()
    const isSmallDesktop = useMediaQuery(`(max-width: ${breakpoints.mapLarge})`)
    const isMobile = useMediaQuery(`(max-width: ${breakpoints.map})`)
    useListingsFavourites()
    const router = useRouter()
    const isChinese = router.locale === 'zh'

    const { setHiddenSearch, listWrapperRef } = useMapList()
    useEffect(() => {
      setTimeout(() => setHiddenSearch(false), 1300)
    }, [setHiddenSearch])

    const virtualizationEnabled = !isMobile
    const columns = isMobile ? 1 : isSmallDesktop ? 2 : 3
    const rowCount = Math.ceil(buildings.length / columns)
    const estimateRowSize = 445

    const rowVirtualizer = useWindowVirtualizer({
      count: rowCount,
      estimateSize: () => estimateRowSize,
      overscan: 2
    })

    const virtualRows = rowVirtualizer.getVirtualItems()
    const totalSize = rowVirtualizer.getTotalSize()

    const listingsCount = metadata.listings_count
    const buildingsCount = metadata.total_count

    const totalPages = Math.ceil(buildingsCount / metadata.page_size)

    const firstItem = (metadata.page - 1) * metadata.page_size + 1
    const lastItem = Math.min(
      metadata.page * metadata.page_size,
      buildingsCount
    )

    const hasCityData = !isEmptyOrNull(city)
    const hasNeighborhoodData = !isEmptyOrNull(neighborhood.data)
    const cityName = city?.translated
    const hoodName = neighborhood?.data?.translated
    const displayName = neighborhood?.data?.translated || city?.translated
    const stateName = city?.translatedState

    const nameWithState = isChinese
      ? `${city.translatedState}${city.translated}`
      : `${city.translated}, ${city.translatedState}`
    const cityNameWithState = hasCityData ? nameWithState : t('seo:Canada')

    const customTitleValue = useMemo(() => {
      if (customTitle) return customTitle
      if (hasNeighborhoodData) {
        const isPoiPage = neighborhood?.data?.type === 'poi'
        const isUniversityPage = neighborhood?.data?.type === 'university'

        if (isPoiPage) {
          if (filterSlug) {
            return `${t(`seo:filter_title_${filterSlug}`, {
              defaultValue: '{{ cityName }}',
              cityName: cityNameWithState
            })} ${t(`seo.pois:title_near_location`, {
              defaultValue: 'near {{ locationName }}',
              locationName: hoodName
            })}`
          } else {
            return (
              <Trans
                i18nKey="seo:search_title_near_neighborhood_city_state"
                values={{ hoodName, cityName, stateName }}
                defaults="Apartments, Condos and Houses for Rent near {{ hoodName }}, {{ cityName }}, {{ stateName}}"
              />
            )
          }
        } else if (isUniversityPage) {
          return hasCityData ? (
            <Trans
              i18nKey="seo:search_title_near_neighborhood_city_state"
              values={{ hoodName, cityName, stateName }}
              defaults="Apartments, Condos and Houses for Rent near {{ hoodName }}, {{ cityName }}, {{ stateName}}"
            />
          ) : (
            <Trans
              i18nKey="seo:search_title_near_neighborhood"
              values={{ hoodName }}
              defaults="Apartments, Condos and Houses for Rent near {{ hoodName }}"
            />
          )
        } else {
          return hasCityData ? (
            <Trans
              i18nKey="seo:search_title_in_neighborhood_city_state"
              values={{ hoodName, cityName, stateName }}
              defaults="Apartments, Condos and Houses for Rent in {{ hoodName }}, {{ cityName }}, {{ stateName}}"
            />
          ) : (
            <Trans
              i18nKey="seo:search_title_in_neighborhood"
              values={{ hoodName }}
              defaults="Apartments, Condos and Houses for Rent in {{ hoodName }}"
            />
          )
        }
      }
      if (filterSlug) {
        return t(`seo:filter_title_${filterSlug}`, {
          defaultValue: '{{ cityName }}',
          cityName: cityNameWithState
        })
      }
      return undefined
    }, [customTitle, neighborhood?.data, city, citySlug, filterSlug, isChinese])

    const handlePageChange = ({ currentPage }) => {
      if (currentPage === metadata.page) return
      filter.setFilter({ page: currentPage })
      listWrapperRef.current.scrollTop = 0
      window.scrollTo({ top: 0, behavior: 'auto' })
    }

    return (
      <ListWrapper isFilterSet={isFilterSet} ref={listWrapperRef}>
        <Box data-impression-container="true">
          <SearchTitleWrapper>
            {customBreadcrumbs ? (
              customBreadcrumbs
            ) : citySlug ? (
              <CityBreadcrumbs
                cityData={city}
                neighborhoodData={neighborhood?.data}
                loading={cityLoading || neighborhood?.isLoading}
                filterSlug={filterSlug}
              />
            ) : null}
            <SearchTitle
              cityName={displayName}
              CustomTitle={customTitleValue}
              loadingCount={loadingBuildings || !markersLoaded}
              listingCount={listingsCount}
              isMapMode
              showSort
            />
          </SearchTitleWrapper>
          {buildings?.length < 1 && !hideEmptyState && !isEmptyOrNull(data) ? (
            <NoResultsWrapper>
              <EmptyState />
            </NoResultsWrapper>
          ) : (
            <>
              <Content>
                {/* <MessageModalProvider> */}
                {markersLoaded && !listingsCount && !loadingBuildings && (
                  <NearbyListingsText>
                    {hasCityData
                      ? t('b.listings.view.explore_nearby_listings.text', {
                          defaultValue:
                            'Explore listings near {{ cityName }} that match your search:',
                          cityName: displayName
                        })
                      : t(
                          'b.listings.view.explore_nearby_listings_general.text',
                          'Explore listings nearby that match your search:'
                        )}
                  </NearbyListingsText>
                )}
                {!hideFeaturedListings && (
                  <FeaturedListings
                    loading={loadingBuildings}
                    listings={featured}
                    maxListing={100}
                    setHighlightedBuilding={setHighlightedBuilding}
                    setVisitedBuilding={setVisitedBuilding}
                  />
                )}

                <RegularListings
                  virtualRows={virtualizationEnabled ? virtualRows : undefined}
                  totalSize={virtualizationEnabled ? totalSize : undefined}
                  buildings={buildings}
                  columns={columns}
                  loading={loadingBuildings}
                  setHighlightedBuilding={setHighlightedBuilding}
                  setVisitedBuilding={setVisitedBuilding}
                  hideFeaturedListings={hideFeaturedListings}
                  onlyVerifiedListings={onlyVerifiedListings}
                  isMobile={isMobile}
                />
                {/* </MessageModalProvider> */}
              </Content>
              <PaginationWrapper>
                <Pagination
                  currentPage={metadata.page}
                  totalRecords={buildingsCount}
                  pageLimit={metadata.page_size}
                  totalPages={totalPages}
                  pageNeighbours={1}
                  onPageChanged={handlePageChange}
                  preload={preload}
                  customStyle={pageButtonsStyle}
                />
                {buildingsCount > metadata.page_size && (
                  <PaginationSummary>
                    {firstItem} - {`${lastItem} `}
                    <Trans
                      i18nKey="b.listings.view.position_in_buildings.text"
                      values={{ totalBuildings: buildingsCount }}
                      defaults="of {{ totalBuildings }} buildings"
                    />
                  </PaginationSummary>
                )}
              </PaginationWrapper>
            </>
          )}
          {!hideSeoContent && (
            <SearchResultsContent
              cityData={city}
              neighborhoodData={neighborhood?.data}
              metadata={metadata}
              filterSlug={filterSlug}
            />
          )}
        </Box>
      </ListWrapper>
    )
  }
)

List.displayName = 'List'

export { List }
